import { ApiResponse } from "@/types";
import { apiSlice } from "@/redux/apiSlice";
import { Facility as FacilityType, FacilityTable } from "@/types/facility";
import {
  FacilityBooking,
  FacilityBookingTable,
} from "@/types/facility/booking";
import {
  FacilitySchedule,
  FacilityScheduleSlot,
  FacilityScheduleSlots,
  FacilityScheduleTable,
} from "@/types/facility/schedule";
import { AddonReturnTable } from "@/types/facility/addon-returns";
import { FacilityUnavailability, FacilityUnavailabilityTable } from "@/types/facility/unavailability";

const userToken = localStorage.getItem("userToken");

export const Facility = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      // ── Facility Endpoints ────────────────────────────────────────────
      getFacilities: builder.query<
        FacilityTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: (result, error, { orgId }) => [
          { type: "Facility", id: `LIST_${orgId}` },
          "Facility",
          "FacilityCategory",
          "IncomeCategory",
        ],
      }),

      getFacilityById: builder.query<
        FacilityType,
        { org_id: number; id: number; query?: string }
      >({
        query: ({ id, org_id, query }) => ({
          url: `/facility/${id}?org_id=${org_id}${query && query.length > 0 ? `&${query}` : ""
            }`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: (result, error, { id, org_id }) => [
          { type: "Facility", id: `${org_id}_${id}` },
          "Facility",
          "FacilityCategory",
          "IncomeCategory",
        ],
        keepUnusedDataFor: 0,
      }),

      createFacility: builder.mutation<ApiResponse, FacilityType>({
        query: (facility) => ({
          url: `/facility`,
          method: "POST",
          body: facility,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),

      updateFacility: builder.mutation<ApiResponse, FacilityType>({
        query: (facility) => ({
          url: `/facility/${facility.id}`,
          method: "PUT",
          body: facility,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility", "FacilityCategory", "IncomeCategory"],
      }),

      deleteFacility: builder.mutation<ApiResponse, number>({
        query: (facility_id) => ({
          url: `/facility/${facility_id}`,
          method: "DELETE",
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),

      // ── Schedule Endpoints ────────────────────────────────────────────
      createFacilitySchedule: builder.mutation<ApiResponse, FacilitySchedule>({
        query: (data) => ({
          url: `/facility/schedule`,
          method: "POST",
          body: data,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),

      updateFacilitySchedule: builder.mutation<ApiResponse, FacilitySchedule>({
        query: (data) => ({
          url: `/facility/schedule/${data.general_info.id}`,
          method: "PUT",
          body: data,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      getFacilityScheduleById: builder.query<
        FacilitySchedule,
        { org_id: number; schedule_id: number; query: string }
      >({
        query: ({ org_id, schedule_id, query }) => ({
          url: `/facility/schedule/${schedule_id}?org_id=${org_id}&${query}`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      deleteFacilitySchedule: builder.mutation<
        ApiResponse,
        {
          schedule_id: number;
          scope: "this" | "this_and_following" | "all";
          start_date: string; // always required
          end_date: string; // always required
        }
      >({
        query: ({ schedule_id, scope, start_date, end_date }) => ({
          url: `/facility/schedule/${schedule_id}?scope=${scope}&start_date=${start_date}&end_date=${end_date}`,
          method: "DELETE",
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      getAllFacilitySchedules: builder.query<
        FacilityScheduleTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility/schedule?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      getAllFacilityScheduleSlot: builder.query<
        FacilityScheduleSlots,
        { query: string; orgId: number; startDate?: string; endDate?: string }
      >({
        query: ({ query, orgId, startDate, endDate }) => {
          let url = `/facility/schedule/slots?org_id=${orgId}${query ? `&${query}` : ""}&load_available=true`;
          if (startDate) url += `&start_date=${startDate}`;
          if (endDate) url += `&end_date=${endDate}`;
          return {
            url,
            method: "GET",
            headers: { Accept: "application/json" },
          };
        },
        providesTags: ["Facility", "IncomeCategory"],
      }),

      getFacilityScheduleSlot: builder.query<
        ApiResponse,
        { schedule_id: number }
      >({
        query: ({ schedule_id }) => ({
          url: `/facility/schedule/slots`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      getFacilityScheduleSlotById: builder.query<
        FacilityScheduleSlot,
        { slotId: number; orgId: number; loadAddsOn: boolean }
      >({
        query: ({ slotId, orgId, loadAddsOn }) => ({
          url: `/facility/schedule/slots/${slotId}?org_id=${orgId}&load_add_ons=${loadAddsOn}`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      addToCartFacilitySlot: builder.mutation<
        ApiResponse,
        {
          org_id: number;
          schedule_id: number;
          date: string;
          start_time: string;
          end_time: string;
          duration_hours: number;
          price_master_id: number;
          max_attendees: number;
          color: string;
          location: string;
          tax_group_id: number;
          income_category_id: number;
          advance_booking_days: number;
          member_id: number;
          addon_ids: number[];
        }
      >({
        query: (data) => ({
          url: `/facility/schedule/slots`,
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["Facility"],
      }),


      getFacilityScheduleSlotDetails: builder.query<
        ApiResponse,
        { org_id: number; schedule_id: number; member_id?: number; start_time: string; end_time: string; load_available?: boolean; load_pricing?: boolean }
      >({
          query: ({ org_id, schedule_id, member_id, start_time, end_time, load_available = false, load_pricing = false }) => {
            const params = new URLSearchParams({
              org_id: String(org_id),
              schedule_id: String(schedule_id),
              start_time,
              end_time,
              load_available: String(load_available),
              load_pricing: String(load_pricing),
            });

            if (member_id !== undefined) {
              params.set("member_id", String(member_id));
            }

            return {
              url: `/facility/schedule/slots/details?${params.toString()}`,
              method: "GET",
              headers: { Accept: "application/json" },
            };
          },
          providesTags: ["Facility", "IncomeCategory"],
          keepUnusedDataFor: 0,
        }),

      getFacilityList: builder.query<
        { id: string; name: string }[],
        { id: number; query: string }
      >({
        query: ({ id, query }) => ({
          url: `/facility?org_id=${id}${query ? `&${query}` : ""}`,
          headers: { Accept: "application/json" },
          providesTags: ["Facility", "IncomeCategory"],
        }),
        transformResponse: (response: {
          data: { id: number; name: string }[];
          total_counts: number;
        }) => {
          return response.data.map((item) => ({
            id: String(item.id),
            name: item.name,
          }));
        },
      }),

      // ── Booking Endpoints ─────────────────────────────────────────────
      createFacilityBooking: builder.mutation<ApiResponse, FacilityBooking>({
        query: (data) => ({
          url: `/bookings`,
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      updateFacilityBooking: builder.mutation<ApiResponse, FacilityBooking>({
        query: (data) => ({
          url: `/bookings/${data.id}`,
          method: "PUT",
          body: data,
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      deleteFacilityBooking: builder.mutation<ApiResponse, number>({
        query: (schedule_id) => ({
          url: `/bookings/${schedule_id}`,
          method: "DELETE",
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      getAllFacilityBookings: builder.query<
        FacilityBookingTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/bookings?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      getFacilityBookingById: builder.query<FacilityBooking, number>({
        query: (id) => ({
          url: `/bookings/${id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      swapMember: builder.mutation<
        ApiResponse,
        { booking_id: number; target_member_id: number; org_id: number }
      >({
        query: ({ booking_id, target_member_id, org_id }) => ({
          url: `/bookings/${booking_id}/swap-member`,
          method: "PATCH",
          body: { target_member_id, org_id },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      qrStatus: builder.query<ApiResponse, { booking_id: number }>({
        query: (booking_id) => ({
          url: `/bookings/${booking_id}/qr-status`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      inviteMemberBooking: builder.mutation<
        ApiResponse,
        { booking_id: number; member_id: number }
      >({
        query: ({ booking_id, member_id }) => ({
          url: `/bookings/${booking_id}/invite`,
          method: "POST",
          body: { member_id },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      updateBookingStatus: builder.mutation<
        ApiResponse,
        {
          booking_id: number;
          action?: "booking_approved" | "booking_rejected";
          payment_action?: string;
          reason?: string;
          org_id: number;
          auth_token?: string;
        }
      >({
        query: ({
          booking_id,
          action,
          payment_action,
          reason,
          org_id,
          auth_token,
        }) => ({
          url: `/bookings/${booking_id}/update-status`,
          method: "PATCH",
          headers: {
            Accept: "application/json",
            Authorization:
              auth_token !== ""
                ? (auth_token as string)
                : (userToken as string),
          },
          body: { action, payment_action, reason, org_id },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      validateBooking: builder.query<
        ApiResponse,
        { org_id: number; schedule_id: number; slot_id: number }
      >({
        query: ({ org_id, schedule_id, slot_id }) => ({
          url: `/bookings/validate-booking-date?org_id=${org_id}&schedule_id=${schedule_id}&slot_id=${slot_id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      criteriaValidation: builder.query<
        ApiResponse,
        { org_id: number; member_id: number; facility_id: number }
      >({
        query: ({ org_id, member_id, facility_id }) => ({
          url: `/bookings/criteria-validation?org_id=${org_id}&member_id=${member_id}&facility_id=${facility_id}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),

      // ── Add-on Returns ────────────────────────────────────────────────
      getAddonReturns: builder.query<
        AddonReturnTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/bookings/add-ons?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
        }),
        providesTags: ["Facility", "IncomeCategory"],
      }),
      updateAddonQuantity: builder.mutation<
        ApiResponse,
        { booking_id: number; addon_id: number; returned_quantity: number }
      >({
        query: ({ booking_id, addon_id, returned_quantity }) => ({
          url: `/bookings/${booking_id}/addons/${addon_id}`,
          method: "PATCH",
          body: { returned_quantity },
        }),
        invalidatesTags: ["Facility", "IncomeCategory"],
      }),

      // ── Facility Unavailability ───────────────────────────────────────
      getFacilityUnavailability: builder.query<
        FacilityUnavailabilityTable,
        { query: string; orgId: number }
      >({
        query: ({ query, orgId }) => ({
          url: `/facility/unavailability?org_id=${orgId}${query ? `&${query}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        }),
        providesTags: ["Facility"],
      }),

      createFacilityUnavailability: builder.mutation<ApiResponse, FacilityUnavailability>({
        query: (data) => ({
          url: `/facility/unavailability`,
          method: "POST",
          body: data,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),

      updateFacilityUnavailability: builder.mutation<
        ApiResponse,
        FacilityUnavailability
      >({
        query: (data) => ({
          url: `/facility/unavailability/${data.id}`,
          method: "PUT",
          body: data,
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),

      deleteFacilityUnavailability: builder.mutation<ApiResponse, number>({
        query: (id) => ({
          url: `/facility/unavailability/${id}`,
          method: "DELETE",
          headers: { Accept: "application/json" },
        }),
        invalidatesTags: ["Facility"],
      }),
    };
  },
});

export const {
  useGetFacilitiesQuery,
  useGetFacilityByIdQuery,
  useCreateFacilityMutation,
  useUpdateFacilityMutation,
  useDeleteFacilityMutation,
  useCreateFacilityScheduleMutation,
  useUpdateFacilityScheduleMutation,
  useGetFacilityScheduleByIdQuery,
  useDeleteFacilityScheduleMutation,
  useGetAllFacilitySchedulesQuery,
  useGetFacilityScheduleSlotQuery,
  useGetFacilityScheduleSlotByIdQuery,
  useGetFacilityListQuery,
  useGetAllFacilityScheduleSlotQuery,
  useCreateFacilityBookingMutation,
  useUpdateFacilityBookingMutation,
  useDeleteFacilityBookingMutation,
  useGetAllFacilityBookingsQuery,
  useGetFacilityBookingByIdQuery,
  useSwapMemberMutation,
  useQrStatusQuery,
  useInviteMemberBookingMutation,
  useUpdateBookingStatusMutation,
  useValidateBookingQuery,
  useCriteriaValidationQuery,
  useLazyCriteriaValidationQuery,
  useLazyGetAllFacilityBookingsQuery,
  useGetAddonReturnsQuery,
  useUpdateAddonQuantityMutation,
  useLazyGetFacilityScheduleSlotDetailsQuery,
  useAddToCartFacilitySlotMutation,
  useGetFacilityUnavailabilityQuery,
  useCreateFacilityUnavailabilityMutation,
  useUpdateFacilityUnavailabilityMutation,
  useDeleteFacilityUnavailabilityMutation,
} = Facility;
